<script>
import Fuse from 'fuse.js'
import { sort } from 'fast-sort'
import { convertToPrimitive, dotAccess } from '@/utils/tools'

export default {
  name: 'ProvideSearch',

  props: {
    searchPattern: {
      type: String,
      default: null,
    },
    records: {
      type: Array,
      required: true,
    },
    /**
     * This prop can be provided in two different ways.
     * Either as an array of strings, or as an array of objects where each object
     * can contain the following properties.
     * name: The property of the records to search by. Can use dot notation in
     *       string format 'path.to.prop' to access nested props
     * sort: This is how the column should be ordered and should be either asc or desc
     *       just leave it empty or don't add it for no sorting
     *       For more details on the sorting see https://github.com/snovakovic/fast-sort
     * convert: This should be a method that will take a record and extracts the property
     *          and convert it to the type needed so that it can be sorted ie.
     *          (record) => Number(record.path.to.date.prop)
     * type: If the type of the prop specified in name is a primitive like a number or string
     *       specify it here so that when sorting it will get converted correctly for comparison.
     *       It should be the type builder or an example of the type ie Number, String, Boolean ...
     *       or 123, 'some string', true/false. If no type is given string is assumed to be the type.
     *
     * For more properties and options see https://fusejs.io/api/options.html#keys
     */
    keys: {
      type: Array,
      default() {
        return []
      },
    },
    // Options for fusejs. Details and documentation here https://fusejs.io/api/options.html
    // Avoid overriding keys with this as keys is also used in sorting.
    options: {
      type: Object,
      default() {
        return {}
      },
    },
    /**
     * If this is set to true this component deactivates all search and sorting and simply returns the records in its
     * slots
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      engine: {}, // We don't initialize the engine here due to issues
    }
  },
  computed: {
    searched() {
      if (!this.disabled && this.searchPattern) {
        return this.engine.search(this.searchPattern).map((rec) => rec.item)
      }
      return this.records
    },
    sorted() {
      if (this.disabled) return this.searched
      const records = this.searched

      const sortSettings = []
      for (const key of this.keys) {
        if (key.sort) {
          sortSettings.push({
            [key.sort]:
              key?.convert ??
              ((rec) =>
                convertToPrimitive(
                  key?.getFn?.(rec) ?? dotAccess(rec, key.name),
                  key.type ?? String,
                )),
          })
        }
      }
      return sortSettings.length === 0 ? records : sort(records).by(sortSettings)
    },
  },
  watch: {
    records: {
      deep: true,
      handler(records) {
        this.engine = this.createEngine(records)
      },
    },
    disabled: {
      handler() {
        this.engine = this.createEngine(this.records)
      },
    },
  },
  created() {
    this.engine = this.createEngine(this.records)
  },
  methods: {
    createEngine(records) {
      if (this.disabled) return {}
      return new Fuse(records, {
        // Set default options here see https://fusejs.io/api/options.html for more
        keys: this.keys,
        threshold: 0.3,
        ...this.options,
      })
    },
  },
  render() {
    return this.$slots.default({
      records: this.searched, // Returns a list of searched values
      sorted: this.sorted, // Returns the searched list in sorted format
    })
  },
}
</script>
